import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Box, InputAdornment, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';

import clsx from 'clsx';

import WithListObservers from '../../../hoc/with-list-observers';
import { AvatarSize, AvatarTypes } from '../../../shared/constants';
import { IUser } from '../../../types/interfaces/user';
import { uniqBy } from '../../../utils/collections/array-utils';
import { getUserViewName } from '../../../utils/common/shared-util';
import { AppBarWithButtons } from '../../designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppUserAvatar from '../../designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppTextField from '../../designe/textfields/app-text-field/AppTextField';
import AppDialog from '../app-dialog/AppDialog';
import { useStyles } from './UserMultiSelectDialog.styles';

export interface IUserMultiSelectDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  users: IUser[];
  selectedUserIds: string[];
  onSave: (selectedIds: string[]) => void | Promise<void>;
  isSaving?: boolean;
  /** When true, the primary action is disabled until at least one user is selected. */
  requireSelection?: boolean;
  emptyStateNotice?: React.ReactNode;
}

function sortUsersByName(a: IUser, b: IUser) {
  const la = a.lastname?.toLowerCase() ?? '';
  const lb = b.lastname?.toLowerCase() ?? '';
  if (la !== lb) {
    return la.localeCompare(lb);
  }
  const fa = a.firstname?.toLowerCase() ?? '';
  const fb = b.firstname?.toLowerCase() ?? '';
  return fa.localeCompare(fb);
}

function userMatchesSearch(user: IUser, q: string) {
  if (!q) {
    return true;
  }
  const name = getUserViewName(user).toLowerCase();
  const login = user.login?.toLowerCase() ?? '';
  return name.includes(q) || login.includes(q);
}

export default function UserMultiSelectDialog({
  open,
  onClose,
  title,
  subtitle,
  users,
  selectedUserIds,
  onSave,
  isSaving = false,
  requireSelection = false,
  emptyStateNotice,
}: IUserMultiSelectDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<string[]>([]);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelection([...selectedUserIds]);
      setSearch('');
    }
    wasOpenRef.current = open;
  }, [open, selectedUserIds]);

  const uniqueSortedUsers = useMemo(() => {
    const unique = uniqBy(users, 'id');
    return [...unique].sort(sortUsersByName);
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return uniqueSortedUsers.filter((u) => userMatchesSearch(u, q));
  }, [uniqueSortedUsers, search]);

  const toggleUser = useCallback((userId: string) => {
    setSelection((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  }, []);

  const handleSave = useCallback(() => {
    void onSave(selection);
  }, [onSave, selection]);

  return (
    <AppDialog open={open} onClose={onClose} title={title}>
      <Box display="flex" flexDirection="column">
        {subtitle ? (
          <Typography
            variant="label-text-md"
            color="text.secondary"
            sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        ) : null}
        <AppTextField
          fullWidth
          placeholder={t('common.searchUsers')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box marginTop={1}>
          {filteredUsers.length === 0 ? (
            <Box className={classes.emptyHintWrapper}>
              {users.length === 0 && emptyStateNotice}
              <Typography className={classes.emptyHint} variant="label-text-md">
                {t('common.noDataToDisplayMessage')}
              </Typography>
            </Box>
          ) : (
            <WithListObservers wrapperClassName={classes.listObserverWrapper}>
              {filteredUsers.map((user) => {
                const selected = selection.includes(user.id);
                return (
                  <Box
                    key={user.id}
                    className={clsx(
                      classes.row,
                      selected && classes.rowSelected,
                    )}
                    onClick={() => toggleUser(user.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleUser(user.id);
                      }
                    }}
                    tabIndex={0}>
                    <AppUserAvatar
                      entity={user}
                      size={AvatarSize.m}
                      type={AvatarTypes.round}
                    />
                    <Box className={classes.rowText}>
                      <Typography
                        variant="text-md"
                        className={classes.userName}>
                        {getUserViewName(user)}
                      </Typography>
                      {user.login ? (
                        <Typography className={classes.userLogin}>
                          @{user.login}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box className={classes.checkIcon}>
                      {selected ? (
                        <CheckCircleIcon color="primary" fontSize="medium" />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{ color: theme.palette.action.disabled }}
                          fontSize="medium"
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </WithListObservers>
          )}
        </Box>

        <AppBarWithButtons
          onCancelClick={onClose}
          onClick={handleSave}
          cancelDisabled={isSaving}
          disabled={isSaving || (requireSelection && selection.length === 0)}
          loading={isSaving}
        />
      </Box>
    </AppDialog>
  );
}
